import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Popper,
  Select,
  SelectProps,
  TextField,
  Typography,
} from "@material-ui/core";
import { useField } from "formik";
import { Options } from "../../../store/types";
import { Autocomplete, AutocompleteProps } from "@material-ui/lab";

export type FormSelectProps = SelectProps & {
  name: string;
  options: Options;
};

export interface FormAutoCompleteProps
  extends Omit<AutocompleteProps<any, false, true, false>, "renderInput"> {
  name: string;
  options: Options;
  label: string;
  onChange?: (e: any) => any;
}
export function FormSelect({
  name,
  fullWidth = true,
  label,
  margin = "dense",
  options = [],
  placeholder = `Select ${name}`,
  style = {},
  ...rest
}: FormSelectProps): JSX.Element {
  const [field, meta] = useField(name);
  const errorText = meta.error && meta.touched ? meta.error : "";
  const hasError = !!errorText;
  return (
    <FormControl
      fullWidth={fullWidth}
      margin={margin}
      error={hasError}
      style={{ marginBottom: "20px" }}
    >
      <Typography style={{ fontWeight: 600, fontFamily: "OpenSans-SemiBold" }}>
        {label}
      </Typography>
      <Select
        variant="outlined"
        {...field}
        {...rest}
        margin="dense"
        style={{
          height: "48px",
          ...style,
        }}
        disabled={options.length === 0 || rest.disabled}
      >
        {options.map((option: { label: string; value: any }) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{errorText}</FormHelperText>
    </FormControl>
  );
}

export default function FormAutoComplete(props: FormAutoCompleteProps) {
  const [field, meta, { setValue }] = useField(props.name);
  const errorText = meta.error && meta.touched ? meta.error : "";
  const hasError = !!errorText;

  if (!props.options.length) {
    return (
      <FormSelect name={props.name} label={props.label} disabled options={[]} />
    );
  }

  return (
    <FormControl
      fullWidth={true}
      error={hasError}
      style={{ marginBottom: "20px" }}
    >
      <Typography
        style={{
          fontWeight: 600,
          fontFamily: "OpenSans-SemiBold",
        }}
      >
        {props.label}
      </Typography>
      <Autocomplete
        {...field}
        {...props}
        options={props.options}
        getOptionLabel={(option) => {
          if (!option) return "";
          const selectedOption = props.options.find(
            ({ value }) => String(value).trim() === String(option).trim()
          );
          if (selectedOption) return selectedOption.label;
          return option.label;
        }}
        includeInputInList
        disableClearable
        id={`id_${props.name}`}
        onChange={(e, option) => {
          if (option) {
            setValue(option.value);
          }
          if (props.onChange && option) {
            const event = {
              target: {
                value: option.value,
              },
            };
            //@ts-ignore
            props.onChange(event);
          }
        }}
        PopperComponent={({ children, ...popperProps }) => (
          <Popper {...popperProps} disablePortal placement="bottom-end">
            {children}
          </Popper>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            error={hasError}
            helperText={errorText}
            variant="outlined"
            style={{
              height: "48px",
            }}
            margin="dense"
          />
        )}
      />
    </FormControl>
  );
}
